import { useFloating, useInteractions, useListNavigation, useTypeahead, useRole, useId } from '@floating-ui/react';
import * as React from 'react';
import { useLanguagePicker, LanguagePickerContext } from './context.js';
import { useListBoxData, ListBox, ListBoxOption } from './list_box.js';
import '../button/index.js';
import { Icon } from '../icons/index.js';
import '../modal/index.js';
import '../stack/index.js';
import '../text/index.js';
import { Button } from '../button/button.js';
import { Modal } from '../modal/modal.js';
import { Text } from '../text/text.js';
import { ModalHeader } from '../modal/modal_header.js';
import { Stack } from '../stack/stack.js';
import { ModalHeading } from '../modal/modal_heading.js';
import { ModalMain } from '../modal/modal_main.js';

const defaultTrigger = ({ formattedLabel }) => {
    return React.createElement(Button, { iconStart: "small-language" }, formattedLabel);
};
/**
 * A widget that allows the user to select their preferred language for the application.
 */
const LanguagePicker = ({ label, onChange, className, value, children, trigger = defaultTrigger, }) => {
    const [open, setOpen] = React.useState(false);
    const enabled = true;
    const [selectedIndex, setSelectedIndex] = React.useState(null);
    const [activeIndex, setActiveIndex] = React.useState(null);
    const { refs, context } = useFloating({
        open: true,
    });
    const handleTypeaheadMatch = React.useCallback((index) => {
        if (open) {
            setActiveIndex(index);
        }
    }, [open]);
    const elementsRef = React.useRef([]);
    const labelsRef = React.useRef([]);
    const { render, find, data } = useListBoxData({ children });
    const selectedItem = React.useMemo(() => find(value), [find, value]);
    React.useLayoutEffect(() => {
        // Wait for labelsRef to be filled.
        queueMicrotask(() => {
            if (open && selectedItem) {
                setSelectedIndex(labelsRef.current.indexOf(selectedItem.label));
            }
        });
    }, [selectedItem, open]);
    const { getFloatingProps, getItemProps } = useInteractions([
        useListNavigation(context, {
            enabled,
            virtual: false,
            listRef: elementsRef,
            activeIndex,
            selectedIndex,
            onNavigate: setActiveIndex,
            focusItemOnHover: false,
        }),
        useTypeahead(context, {
            enabled,
            listRef: labelsRef,
            activeIndex,
            selectedIndex,
            onMatch: handleTypeaheadMatch,
        }),
        useRole(context, { enabled, role: 'listbox' }),
    ]);
    const handleChangeLanguage = React.useCallback(async (lang) => {
        setOpen(false);
        await onChange(lang);
    }, [onChange]);
    const state = useLanguagePicker({ getItemProps });
    const id = useId();
    const labelId = `${id}-label`;
    return (React.createElement(React.Fragment, null,
        render,
        React.createElement(Modal, { size: "small", trigger: trigger({
                label,
                selectedItem,
                formattedLabel: (React.createElement(React.Fragment, null,
                    label,
                    ": ",
                    React.createElement(Text, { weight: "regular" }, selectedItem?.label))),
            }), open: open, onToggle: setOpen, className: className },
            React.createElement(ModalHeader, null,
                React.createElement(Stack, { alignItems: "center", gap: "small" },
                    React.createElement(Icon, { name: "small-language", size: 24, prominence: "default" }),
                    React.createElement(ModalHeading, null,
                        React.createElement("span", { id: labelId }, label)))),
            React.createElement(ModalMain, null,
                React.createElement(LanguagePickerContext.Provider, { value: state },
                    React.createElement(ListBox, { "aria-labelledby": labelId, value: value, onSelect: handleChangeLanguage, elementsRef: elementsRef, labelsRef: labelsRef, activeIndex: activeIndex, data: data, ...getFloatingProps({
                            ref: refs.setFloating,
                        }) }, children))))));
};
const LanguagePickerItem = React.forwardRef(function LanguagePickerItem(props, ref) {
    const ctx = React.useContext(LanguagePickerContext);
    return React.createElement(ListBoxOption, { ...props, ref: ref, lang: props.value, ...ctx?.getItemProps(props) });
});

export { LanguagePicker, LanguagePickerItem };
